import { useState, useEffect, useRef, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { searchOrgMembers, type OrgMember } from '../../services/chatChannelService';
import { useOrgStore } from '../../store/orgStore';
import Avatar from '../common/Avatar';
import type { ProfileColorKey } from '@maios/shared';

interface Props {
  selectedIds: string[];
  onAdd: (member: OrgMember) => void;
  onRemove: (userId: string) => void;
  excludeIds?: string[];
  lockedIds?: string[];
  maxCount?: number;
  placeholder?: string;
}

export default function MemberSearchInput({ selectedIds, onAdd, onRemove, excludeIds = [], lockedIds = [], maxCount, placeholder }: Props) {
  const orgId = useOrgStore((s) => s.activeOrgId);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: allMembers } = useQuery({
    queryKey: ['orgMembers', orgId],
    queryFn: () => searchOrgMembers(orgId!),
    enabled: !!orgId,
    staleTime: 60_000,
  });

  const filtered = useMemo(() => {
    if (!allMembers) return [];
    const excluded = new Set([...selectedIds, ...excludeIds]);
    let result = allMembers.filter((m) => !excluded.has(m.userId));
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((m) => m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q));
    }
    return result.slice(0, 10);
  }, [allMembers, selectedIds, excludeIds, search]);

  const selectedMembers = useMemo(() => {
    if (!allMembers) return [];
    return selectedIds.map((id) => allMembers.find((m) => m.userId === id)).filter(Boolean) as OrgMember[];
  }, [allMembers, selectedIds]);

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const atMax = maxCount !== undefined && selectedIds.length >= maxCount;

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
        <input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder ?? '이름 또는 이메일 검색...'}
          disabled={atMax}
          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50"
        />
      </div>

      {open && filtered.length > 0 && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filtered.map((m) => (
            <button key={m.userId} type="button"
              onClick={() => { onAdd(m); setSearch(''); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-gray-50 transition-colors">
              <Avatar
                size="sm"
                userId={m.userId}
                name={m.name}
                avatarUrl={m.avatarUrl}
                displayColor={m.displayColor as ProfileColorKey | null | undefined}
                firstName={m.firstName}
              />
              <div className="min-w-0">
                <p className="text-sm text-gray-900 truncate">{m.name}</p>
                <p className="text-sm text-gray-500 truncate">{m.email}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {selectedMembers.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selectedMembers.map((m) => (
            <span key={m.userId}
              className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 rounded-full text-sm text-gray-700">
              {m.name}
              {!lockedIds.includes(m.userId) && (
                <button type="button" onClick={() => onRemove(m.userId)} className="text-gray-400 hover:text-red-500">
                  <X className="size-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
